import { useMemo } from 'react';

import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { Uploads } from './file-history';
import { Issues } from './issues';
import { Metadata } from './metadata';
import { RelativitySync } from './relativity-sync';
import { Users } from './users';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { AuditTrailTable } from '../../../components/audit-trail-table';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

enum SettingsTab {
  Metadata = 'metadata',
  Issues = 'issues',
  RelativitySync = 'relativity-sync',
  FileHistory = 'file-history',
  Users = 'users',
  MatterAuditTrail = 'audit-trail',
}

interface SettingsTabDef {
  id: SettingsTab;
  title: string;
  icon: React.ReactNode;
  permission?: Permission | Permission[];
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
}

const tabs: SettingsTabDef[] = [
  {
    id: SettingsTab.Metadata,
    title: 'Metadata',
    icon: <Icon className="lucide--info" />,
    permission: Permission.MatterEdit,
  },
  {
    id: SettingsTab.Issues,
    title: 'Issues',
    icon: <Icon className="lucide--pen-line" />,
    permission: Permission.MatterEdit,
  },
  {
    id: SettingsTab.RelativitySync,
    title: 'Relativity Sync',
    icon: <Icon className="lucide--refresh-cw" />,
    permission: Permission.RelativityImport,
  },
  {
    id: SettingsTab.Users,
    title: 'Users',
    icon: <Icon className="lucide--users-round" />,
    permission: Permission.MatterViewUsers,
  },
  {
    id: SettingsTab.FileHistory,
    title: 'File History',
    icon: <Icon className="lucide--upload" />,
    permission: Permission.MatterCreate,
  },
  {
    id: SettingsTab.MatterAuditTrail,
    title: 'Audit Trail',
    icon: <Icon className="lucide--footprints" />,
    permission: Permission.AuditView,
  },
];

function hasAnyPermission(checkPermission: (p: Permission) => boolean, permission: Permission | Permission[]) {
  const permissions = Array.isArray(permission) ? permission : [permission];
  return permissions.some(checkPermission);
}

function PermissionRoute({
  permission,
  children,
}: {
  permission: Permission | Permission[];
  children: React.ReactNode;
}) {
  const { matterId } = useParams();
  const { user } = useUserContext();
  const { hasPermission: checkPermission } = useHasPermission();

  // Wait for user data before making a permission decision
  // Not super necessary as user should always be defined in order to get here, though
  if (!user) return null;

  if (!hasAnyPermission(checkPermission, permission)) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }

  return <>{children}</>;
}

function SettingsRedirect() {
  const { hasPermission: checkPermission } = useHasPermission();

  const firstAllowedTab = tabs.find((tab) => !tab.permission || hasAnyPermission(checkPermission, tab.permission));

  if (firstAllowedTab) {
    return <Navigate to={firstAllowedTab.id} replace />;
  }

  return <Navigate to="/matters" replace />;
}

function SettingsTabItem({ tab }: { tab: SettingsTabDef }) {
  const { hasPermission: checkPermission } = useHasPermission();
  const allowed = !tab.permission || hasAnyPermission(checkPermission, tab.permission);
  const uiMode = tab.uiMode ?? 'hidden';
  const isHidden = !allowed && uiMode === 'hidden';
  const isDisabled = !allowed && uiMode === 'disabled';

  if (isHidden) return null;

  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <TabsTrigger
              value={tab.id}
              disabled
              className="pb-2.5 text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
            >
              {tab.title}
            </TabsTrigger>
          </div>
        </TooltipTrigger>
        <TooltipContent>{tab.disabledMessage ?? 'You do not have permission to access this'}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <TabsTrigger
      asChild
      value={tab.id}
      className="pb-2.5 text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
    >
      <Link to={tab.id}>{tab.title}</Link>
    </TabsTrigger>
  );
}

export function MatterSettings() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { data: matter } = useGetMatterV2(matterId);

  const location = useLocation();

  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');

    for (const tab of tabs) {
      if (pathSegments.includes(tab.id)) {
        return tab.id;
      }
    }
  }, [location.pathname]);

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${matter.id}`,
                  label: matter.name,
                  active: true,
                  iconClass: 'lucide--folder',
                },
                { href: `/matters/${matter.id}/settings`, label: 'Settings', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text="Matter Settings" />
        </PageHeader>

        <Tabs value={activeTab}>
          <TabsList className="mb-3 w-full justify-start gap-4">
            {tabs.map((tab) => (
              <SettingsTabItem key={tab.id} tab={tab} />
            ))}
          </TabsList>
        </Tabs>
      </PageContainer>

      <div className="flex min-h-0 flex-1 flex-col px-10 py-4">
        <Routes>
          <Route index element={<SettingsRedirect />} />
          <Route
            path="metadata"
            element={
              <PermissionRoute permission={Permission.MatterEdit}>
                <Metadata />
              </PermissionRoute>
            }
          />
          <Route
            path="issues"
            element={
              <PermissionRoute permission={Permission.MatterEdit}>
                <Issues />
              </PermissionRoute>
            }
          />
          <Route
            path="relativity-sync"
            element={
              <PermissionRoute permission={Permission.RelativityImport}>
                <RelativitySync />
              </PermissionRoute>
            }
          />
          <Route
            path="file-history/*"
            element={
              <PermissionRoute permission={Permission.MatterCreate}>
                <Uploads />
              </PermissionRoute>
            }
          />
          <Route
            path="users"
            element={
              <PermissionRoute permission={Permission.MatterViewUsers}>
                <Users />
              </PermissionRoute>
            }
          />
          <Route
            path="audit-trail"
            element={
              <PermissionRoute permission={Permission.AuditView}>
                <AuditTrailTable grammar={'this'} matterId={matterId} />
              </PermissionRoute>
            }
          />
        </Routes>
      </div>
    </>
  );
}
